import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import { preReactInitialData } from "../App"
import InitialData from "../appData/initialData"
import { Plan, ProductData } from "../appData/productData"
import native from "../native/native"
import debugData from "../utils/debugData"
import { getCurrentPageLoadedParams } from "./loadedParams"
import useQueryParams from "../hooks/useQueryParams"

interface State {
  initialData?: InitialData
  productData?: ProductData
  fullyLoaded: boolean
  headerInsetColor?: string
  footerInsetColor?: string
  ignoreHeaderInset: boolean
  ignoreFooterInset: boolean
  purchaseCancelled: boolean
}

interface Actions {
  setHeaderInsetColor: (color: string) => void
  setFooterInsetColor: (color: string) => void
  setIgnoreHeaderInset: (ignore: boolean) => void
  setIgnoreFooterInset: (ignore: boolean) => void
}

interface Context {
  state: State
  actions: Actions
}

const AppStateContext = createContext<Context | null>(null)

interface Props extends React.PropsWithChildren {
  sendAllProductData: boolean
  isPromo: boolean
  requiredProductIds?: string[]
  requireFreeTrialStatus?: boolean
  styleConfigCreator: (data: InitialData) => any
}

function updatePlanDurations(plan: Plan) {
  plan.monthlyProduct.duration = "monthly"
  plan.yearlyProduct.duration = "yearly"
  plan.threeMonthsProduct.duration = "threeMonths"
}

const AppStateProvider = (props: Props) => {
  const [initialData, setInitialData] = useState<InitialData | undefined>(
    undefined,
  )
  const [productData, setProductData] = useState<ProductData | undefined>(
    debugData.getProductData(null),
  )
  const [headerInsetColor, setHeaderInsetColor] = useState<string | undefined>(
    undefined,
  )
  const [footerInsetColor, setFooterInsetColor] = useState<string | undefined>(
    undefined,
  )
  const [ignoreFooterInset, setIgnoreFooterInset] = useState<boolean>(false)
  const [ignoreHeaderInset, setIgnoreHeaderInset] = useState<boolean>(false)

  const [purchaseCancelledState, setPurchaseCancelledState] =
    useState<boolean>(false)

  const styleConfigCreator = props.styleConfigCreator

  const initialDataLoad = useCallback(
    (data: InitialData) => {
      if (data.darkMode) {
        document.documentElement.setAttribute("data-theme", "dark")
      }

      setInitialData(data)

      native.postNativeEvent("styleConfig", styleConfigCreator(data))
    },
    [styleConfigCreator],
  )

  const productDataLoad = useCallback((data: ProductData) => {
    console.log("Product Data Load")
    data.ultimate.isUltimate = true
    updatePlanDurations(data.premium)
    updatePlanDurations(data.ultimate)
    setProductData({ ...data, loaded: true })
  }, [])

  const purchaseCancelled = useCallback(() => {
    setPurchaseCancelledState(true)
  }, [])

  const queryParams = useQueryParams()

  useEffect(() => {
    const anyWindow = window as any

    anyWindow.initialDataLoad = initialDataLoad
    anyWindow.productDataLoad = productDataLoad
    anyWindow.purchaseCancelled = purchaseCancelled

    const loadedParams = getCurrentPageLoadedParams()

    native.postNativeEvent("loaded", loadedParams)

    if (preReactInitialData != null) {
      initialDataLoad(preReactInitialData)
    }

    if (queryParams.debug) {
      initialDataLoad(debugData.getInitialData(queryParams))
      productDataLoad(debugData.getProductData(queryParams))
    }
  }, [])

  let initialDataToUse = initialData
  if (initialDataToUse != null) {
    if (initialDataToUse.originalBottomInset == null) {
      initialDataToUse.originalBottomInset = initialDataToUse.bottomInset
      initialDataToUse.originalTopInset = initialDataToUse.topInset
    }

    initialDataToUse.bottomInset = ignoreFooterInset
      ? 0
      : initialDataToUse.bottomInset
    initialDataToUse.topInset = ignoreHeaderInset
      ? 0
      : initialDataToUse.topInset
  }

  const state: State = {
    initialData: initialDataToUse,
    productData,
    fullyLoaded: initialData != null && productData != null,
    headerInsetColor,
    footerInsetColor,
    ignoreFooterInset,
    ignoreHeaderInset,
    purchaseCancelled: purchaseCancelledState,
  }

  const actions: Actions = {
    setHeaderInsetColor,
    setFooterInsetColor,
    setIgnoreFooterInset,
    setIgnoreHeaderInset,
  }

  return (
    <AppStateContext.Provider value={{ state, actions }}>
      {props.children}
    </AppStateContext.Provider>
  )
}

const useAppState = () => {
  const context = useContext(AppStateContext)

  if (!context) {
    throw new Error(
      "useAppState has to be used within <AppStateContext.Provider>",
    )
  }

  return context
}

export { AppStateProvider, AppStateContext, useAppState }
